import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Calculator as CalcIcon, FileText, CheckCircle2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import CampaignBanner from "@/components/CampaignBanner";
import CalculatorSwitcher from "@/components/CalculatorSwitcher";
import TransferCalculator from "@/components/TransferCalculator";
import ChatWidget from "@/components/ChatWidget";

/**
 * Calculators landing page — Transfer cost variant.
 *
 * Final layout (post UX-refinement):
 *   ┌────────────────────────────────────────────────────────────┐
 *   │ SiteHeader · CampaignBanner · CalculatorSwitcher            │
 *   ├────────────────────────────────────────────────────────────┤
 *   │ Compact horizontal headline band (kicker · h1 · one-liner)  │
 *   ├────────────────────────────────────────────────────────────┤
 *   │                                                              │
 *   │ ╔══════════════════════════════════════════════════════════╗ │
 *   │ ║  INPUT (left, 5/12)   │   OUTPUT (right, 7/12)            ║ │
 *   │ ║  — full horizontal width, dominant primary action —       ║ │
 *   │ ╚══════════════════════════════════════════════════════════╝ │
 *   │                                                              │
 *   ├────────────────────────────────────────────────────────────┤
 *   │ SUPPORTING CONTENT (below calc)                             │
 *   │   · Fact bullets · How it works · What happens next         │
 *   │   · Trust badges                                            │
 *   └────────────────────────────────────────────────────────────┘
 */
export default function CalculatorPage() {
  return (
    <div className="min-h-screen bg-background grain" data-testid="calculators-page">
      <SiteHeader />
      <CampaignBanner kicker="EH Inc · Calculators" />
      <CalculatorSwitcher active="transfer" />

      {/* COMPACT HEADLINE BAND — horizontal, doesn't push calc down */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-8 pb-6 lg:pt-10 lg:pb-7">
          <div className="flex flex-wrap items-end justify-between gap-x-10 gap-y-3">
            <div className="min-w-0">
              <div className="text-overline text-zinc-500" data-testid="calculators-hero-kicker">
                Calculators · Property Transfer
              </div>
              <h1 className="mt-2 font-serif text-3xl lg:text-4xl xl:text-5xl font-medium tracking-tight leading-[1.05]">
                Know the full cost <span className="text-zinc-500">before you sign.</span>
              </h1>
            </div>
            <p className="text-sm lg:text-base text-zinc-600 leading-relaxed max-w-md">
              SARS transfer duty, conveyancing fees, deeds office and disbursements — calculated to current 2025 rates.
            </p>
          </div>
        </div>
      </section>

      {/* CALCULATOR — full horizontal width, primary action */}
      <section className="bg-white border-b border-zinc-200" data-testid="calculators-transfer-anchor">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-6 pb-12 lg:pt-8 lg:pb-16">
          <TransferCalculator />
        </div>
      </section>

      {/* SUPPORTING CONTENT — below calculator (fact bullets + how-it-works + steps) */}
      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-3 gap-10 lg:gap-14">
          {/* Statutory facts */}
          <div>
            <div className="text-overline text-zinc-500">What's included</div>
            <h2 className="mt-3 font-serif text-2xl lg:text-3xl font-medium tracking-tight leading-snug">
              Built on the South African transfer-cost statute.
            </h2>
            <ul className="mt-6 space-y-3 text-sm text-zinc-700">
              <FactRow text="SARS transfer duty (statutory, no markup)" />
              <FactRow text="Conveyancing tariff (LSPC sliding scale)" />
              <FactRow text="Deeds office, FICA, rates clearance & levies" />
              <FactRow text="Optional bond registration costs" />
            </ul>
          </div>

          {/* How it works */}
          <div>
            <div className="text-overline text-zinc-500">How this calculation works</div>
            <h3 className="mt-3 font-serif text-2xl font-medium leading-snug">Real numbers, no marketing estimate.</h3>
            <p className="mt-5 text-zinc-600 leading-relaxed text-sm">
              We apply the current SARS Transfer Duty Table (R1.1m threshold, sliding scale from 3% upward), legal-cost
              regulations (Sheriff &amp; Conveyancer Tariff), deeds office charges, and standard FICA / Rates / Levy
              disbursements. The result is an itemised quote you can hand to your bond originator on day one.
            </p>
          </div>

          {/* What happens next */}
          <div className="bg-white border border-zinc-200 p-8 lg:p-9">
            <div className="text-overline text-zinc-500">What happens next</div>
            <h3 className="mt-3 font-serif text-2xl font-medium">From estimate to lodged transfer.</h3>
            <ol className="mt-6 space-y-5">
              <Step n="01" head="Get your estimate" body="The breakdown is yours to keep, share or send to your bond originator." />
              <Step n="02" head="Open the matter" body="10-minute guided intake — FICA, OTP and seller info collected online." />
              <Step n="03" head="Attorney review (1 working day)" body="An admitted conveyancer signs off the engagement letter." />
              <Step n="04" head="Live status until lodgement" body="Tracked via your portal + WhatsApp. ~14 working days, Cape Town." />
            </ol>
            <Link
              to="/intake"
              className="mt-8 inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-3.5 text-sm font-medium hover:bg-zinc-800 transition-colors"
              data-testid="calculators-cta-intake"
            >
              Continue into intake <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Trust / Compliance footer */}
      <section className="bg-white border-b border-zinc-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12 grid sm:grid-cols-3 gap-10">
          <TrustBadge Icon={ShieldCheck} title="FICA-compliant" body="Encrypted intake, audit-logged uploads." />
          <TrustBadge Icon={FileText}    title="Audit-trailed drafts" body="Every draft carries a DRAFT marker until sign-off." />
          <TrustBadge Icon={CalcIcon}    title="Transparent pricing" body="Itemised quotes, no hidden surcharges." />
        </div>
      </section>

      <SiteFooter />
      <ChatWidget />
    </div>
  );
}


/* ── helpers ──────────────────────────────────────────────────────────── */

function FactRow({ text }) {
  return (
    <li className="flex items-start gap-2.5">
      <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0 text-zinc-400" />
      <span>{text}</span>
    </li>
  );
}

function Step({ n, head, body }) {
  return (
    <li className="grid grid-cols-12 gap-3">
      <div className="col-span-2 font-mono text-xs text-zinc-400 pt-0.5">{n}</div>
      <div className="col-span-10">
        <div className="font-semibold text-sm text-zinc-900">{head}</div>
        <div className="text-zinc-600 text-xs leading-relaxed mt-1">{body}</div>
      </div>
    </li>
  );
}

function TrustBadge({ Icon, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-5 w-5 mt-0.5 text-zinc-400" />
      <div>
        <div className="font-semibold text-sm text-zinc-900">{title}</div>
        <div className="text-zinc-600 text-xs leading-relaxed mt-1">{body}</div>
      </div>
    </div>
  );
}
